import { TransactionResponse } from '@ethersproject/abstract-provider';
import { BigNumber } from '@ethersproject/bignumber';
import { URLs } from '@manifoldxyz/js-ts-utils';
import { ClaimStudioInstance, ScribeStudioInstance } from '@/api/models/studioInstance';
import { DEFAULT_STEP_STATE } from '@/common/constants';
import networkCheck from '@/common/networkCheck';
import {
  StepConfig,
  TransactionStep,
  TransactionStepState,
  TxStepId,
  TxStepStatus,
} from '@/common/types';
import { getExtensionAddress } from '@/common/util';
import { getTransactionErrorMessage } from '@/common/web3TransactionErrorHandling';
import ERC20Contract from '@/contracts/erc20Contract';
import useMainStore, { StoreType } from '@/store/mainStore';

export function createApproveERC20Step(
  studioInstance: ClaimStudioInstance | ScribeStudioInstance,
  overrides?: {
    config?: Partial<StepConfig>;
    state?: Partial<TransactionStepState>;
  },
): TransactionStep {
  return {
    state: {
      ...DEFAULT_STEP_STATE,
      descriptionByStatus: {
        [TxStepStatus.Unstarted]: 'Approve spending your ERC20',
        [TxStepStatus.Pending]: 'Approving ERC20 spending...',
        [TxStepStatus.Confirming]: 'Waiting for blockchain confirmation...',
        [TxStepStatus.Confirmed]: 'ERC20 approved',
        [TxStepStatus.Failed]: 'ERC20 approval failed',
      },
      ...overrides?.state,
    },
    config: {
      id: TxStepId.ApproveERC20Spending,
      name: 'Approve Spending',
      hideOnceCompleted: true,
    },

    validateBefore: async (store) => {
      const walletAddress = store.walletAddress;
      if (!walletAddress) {
        return { error: 'No wallet address found for approval' };
      }

      const erc20Contract = new ERC20Contract(
        studioInstance.publicData.network,
        studioInstance.publicData.mintPrice.erc20,
      );

      const allowance = await erc20Contract.getAllowance(
        getExtensionAddress(studioInstance),
        walletAddress,
      );

      // now get the total cost of the mint in the erc20's value
      const totalCostBN = BigNumber.from(studioInstance.publicData.mintPrice.value).mul(
        useMainStore.getState().numberOfMints,
      );

      if (allowance.gte(totalCostBN)) {
        return {
          skip: true,
        };
      }
    },
    execute: async (
      _: StoreType,
      updateStepState: (updates: Partial<TransactionStepState>) => void,
    ) => {
      const walletAddress = useMainStore.getState().walletAddress;

      if (!studioInstance.publicData.mintPrice.erc20) {
        throw new Error('No ERC20 address found for approval');
      }

      if (!walletAddress) {
        throw new Error('No wallet address found for approval');
      }

      await networkCheck(studioInstance.publicData.network);

      let approvalTx: TransactionResponse | undefined;
      try {
        const erc20Contract = new ERC20Contract(
          studioInstance.publicData.network,
          studioInstance.publicData.mintPrice.erc20,
        );

        const allowance = await erc20Contract.getAllowance(
          getExtensionAddress(studioInstance),
          walletAddress,
        );

        // now get the total cost of the mint in the erc20's value
        const totalCostBN = BigNumber.from(studioInstance.publicData.mintPrice.value).mul(
          useMainStore.getState().numberOfMints,
        );

        if (allowance.lt(totalCostBN)) {
          // approve our claim contract to spend the erc20
          approvalTx = await erc20Contract.approve(
            walletAddress,
            getExtensionAddress(studioInstance),
          );
        }
      } catch (error) {
        throw new Error(getTransactionErrorMessage(error));
      }

      // mark that the approval step is Confirming
      updateStepState({
        status: TxStepStatus.Confirming,
      });

      if (approvalTx) {
        try {
          try {
            await approvalTx.wait(1);
            updateStepState({
              etherscanLink: URLs.getEtherscanURLForTransactionHash(
                studioInstance.publicData.network,
                approvalTx.hash,
              ),
              txHash: approvalTx.hash,
            });
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
          } catch (error: any) {
            // Handle speed-up case which is a false error in a sense
            if (
              error?.code === 'TRANSACTION_REPLACED' &&
              !error.cancelled &&
              error.replacement?.hash
            ) {
              updateStepState({
                etherscanLink: URLs.getEtherscanURLForTransactionHash(
                  studioInstance.publicData.network,
                  error.replacement.hash,
                ),
                txHash: error.replacement.hash,
              });
              // Wait for the replacement transaction
              const provider = window.ManifoldEthereumProvider.provider();
              if (!provider) throw new Error('No provider available');
              const replacementTx = await provider.getTransaction(error.replacement.hash);
              await replacementTx.wait(1);
            } else {
              // otherwise just rethrow the error for getTransactionErrorMessage to handle
              throw error;
            }
          }
        } catch (error) {
          throw new Error(getTransactionErrorMessage(error));
        }
      }
    },
  };
}
